import { useState, useEffect } from 'react';
import { Bot, Play, Loader2, Search, Users, GitMerge, Clock, CheckCircle, AlertTriangle, RefreshCw } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { toast } from 'sonner';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

function authHeaders() {
  return { 'Content-Type': 'application/json', 'Authorization': `Bearer ${localStorage.getItem('olympus_token')}` };
}

const MODE_INFO = {
  full: { label: 'Komplett', desc: 'Recherche + Dubletten + Zuordnung', icon: Bot },
  research: { label: 'Recherche', desc: 'Online-Recherche (nur Institutionsdaten)', icon: Search },
  duplicates: { label: 'Dubletten', desc: 'KI-Erkennung & Auto-Merge', icon: GitMerge },
  assign: { label: 'Zuordnung', desc: 'Betreute ↔ Institutionen verknüpfen', icon: Users },
};

const ACTION_ICONS = {
  recherche: Search,
  hierarchie_zuordnung: GitMerge,
  dublette_gemergt: GitMerge,
  verknuepfung_erstellt: Users,
  verknuepfung_aktualisiert: Users,
};

export function StewardPanel() {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [logs, setLogs] = useState([]);
  const [loadingLogs, setLoadingLogs] = useState(true);

  const loadLogs = async () => {
    try {
      const res = await fetch(`${API_URL}/api/institutionen/steward/log?limit=5`, { headers: authHeaders() });
      if (res.ok) setLogs(await res.json());
    } catch { /* ignore */ }
    setLoadingLogs(false);
  };

  useEffect(() => { loadLogs(); }, []);

  const runSteward = async (mode) => {
    setRunning(true);
    setResult(null);
    try {
      const res = await fetch(`${API_URL}/api/institutionen/steward/run-sync?mode=${mode}`, {
        method: 'POST', headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Fehler');
      setResult(data);
      toast.success(`Steward: ${data.aktionen?.length || 0} Aktionen durchgeführt`);
      loadLogs();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="space-y-4 p-4" data-testid="steward-panel">
      <div className="flex items-center gap-2 mb-1">
        <Bot className="w-5 h-5 text-violet-500" />
        <span className="font-semibold text-slate-800">KI-Datensteward</span>
        <Badge variant="outline" className="text-[10px] text-violet-600 border-violet-300">Gemma4 lokal</Badge>
      </div>
      <p className="text-xs text-slate-500">
        Lokale KI pflegt Institutionsdaten: Online-Recherche (DSGVO-sicher, keine Personendaten),
        Dubletten-Erkennung, Hierarchie-Aufbau, Betreute-Zuordnung.
      </p>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-2">
        {Object.entries(MODE_INFO).map(([mode, info]) => {
          const Icon = info.icon;
          return (
            <Button key={mode} variant="outline" size="sm" disabled={running}
              onClick={() => runSteward(mode)}
              className="flex flex-col items-center gap-1 h-auto py-3 text-xs hover:border-violet-400 hover:bg-violet-50"
              data-testid={`steward-run-${mode}`}>
              {running ? <Loader2 className="w-4 h-4 animate-spin" /> : <Icon className="w-4 h-4 text-violet-500" />}
              <span className="font-medium">{info.label}</span>
              <span className="text-[10px] text-slate-400 text-center">{info.desc}</span>
            </Button>
          );
        })}
      </div>

      {result && (
        <Card className={result.fehler?.length > 0 ? 'border-amber-200' : 'border-emerald-200'}>
          <CardHeader className="py-2 px-3">
            <CardTitle className="text-sm flex items-center gap-2">
              <CheckCircle className="w-4 h-4 text-emerald-500" />
              Ergebnis: {result.aktionen?.length || 0} Aktionen
              {result.fehler?.length > 0 && (
                <Badge className="bg-amber-100 text-amber-700 text-[10px] ml-auto">{result.fehler.length} Fehler</Badge>
              )}
              {!result.ollama_verfuegbar && (
                <Badge className="bg-red-100 text-red-700 text-[10px] ml-auto">Ollama offline</Badge>
              )}
            </CardTitle>
          </CardHeader>
          <CardContent className="px-3 py-2 space-y-1 max-h-60 overflow-y-auto">
            {result.aktionen?.map((a, i) => {
              const Icon = ACTION_ICONS[a.typ] || CheckCircle;
              return (
                <div key={i} className="text-xs flex items-start gap-2 py-1 text-slate-700">
                  <Icon className="w-3.5 h-3.5 mt-0.5 text-violet-500 flex-shrink-0" />
                  <div>
                    <span className="font-medium">{a.typ.replace(/_/g, ' ')}</span>
                    {a.institution && <span className="text-slate-500"> — {a.institution}</span>}
                    {a.klient && <span className="text-slate-500"> — {a.klient}</span>}
                    {a.aktenzeichen && <Badge variant="outline" className="text-[9px] ml-1">{a.aktenzeichen}</Badge>}
                    {a.konfidenz && <Badge variant="secondary" className="text-[9px] ml-1">{Math.round(a.konfidenz * 100)}%</Badge>}
                  </div>
                </div>
              );
            })}
            {(!result.aktionen || result.aktionen.length === 0) && (
              <p className="text-xs text-slate-400">Keine Änderungen nötig — Daten sind aktuell.</p>
            )}
            {result.fehler?.map((f, i) => (
              <div key={`err-${i}`} className="text-xs flex items-center gap-2 py-1 text-amber-700">
                <AlertTriangle className="w-3 h-3 flex-shrink-0" /> {f}
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="py-2 px-3">
          <CardTitle className="text-sm flex items-center gap-2">
            <Clock className="w-4 h-4" /> Letzte Durchläufe
            <Button size="sm" variant="ghost" className="ml-auto h-6 w-6 p-0" onClick={loadLogs}>
              <RefreshCw className="w-3 h-3" />
            </Button>
          </CardTitle>
        </CardHeader>
        <CardContent className="px-3 py-2 space-y-2 max-h-48 overflow-y-auto">
          {loadingLogs ? (
            <Loader2 className="w-4 h-4 animate-spin text-slate-400 mx-auto" />
          ) : logs.length === 0 ? (
            <p className="text-xs text-slate-400 text-center py-2">Noch keine Durchläufe</p>
          ) : logs.map((log, i) => {
            const r = log.ergebnis || {};
            const aktionen = r.aktionen?.length || 0;
            const fehler = r.fehler?.length || 0;
            return (
              <div key={i} className="text-xs border rounded p-2 bg-slate-50">
                <div className="flex items-center justify-between">
                  <span className="font-medium text-slate-700">{r.modus || '?'}</span>
                  <span className="text-[10px] text-slate-400">{log.timestamp ? new Date(log.timestamp).toLocaleString('de') : ''}</span>
                </div>
                <div className="flex gap-2 mt-1">
                  <Badge variant="secondary" className="text-[10px]">{aktionen} Aktionen</Badge>
                  {fehler > 0 && <Badge className="bg-amber-100 text-amber-700 text-[10px]">{fehler} Fehler</Badge>}
                  {!r.ollama_verfuegbar && <Badge className="bg-red-100 text-red-700 text-[10px]">Ollama offline</Badge>}
                </div>
              </div>
            );
          })}
        </CardContent>
      </Card>
    </div>
  );
}
